import { useEffect, useMemo } from 'react';
import { QuantityControl } from './QuantityControl';
import { PartCombobox } from './PartCombobox';
import { clientUuid } from '../lib/id';
import type { PartReplacementLine, PartSelection, StockItem } from '../types';

interface Props {
  stock: StockItem[];
  catalog: StockItem[];
  value: PartReplacementLine[];
  onChange: (value: PartReplacementLine[]) => void;
}

function newLine(): PartReplacementLine {
  return { replacementId: clientUuid() };
}

function selectedPart(items: StockItem[], partId: string, qty: number): PartSelection | undefined {
  const item = items.find((row) => row.partId === partId);
  if (!item || qty <= 0) return undefined;
  return { partId: item.partId, partCode: item.partCode, partName: item.partName, qty };
}

function normalizedPartCode(value = '') {
  return value.trim().toLocaleLowerCase('ro');
}

function mergeRemovalCatalog(catalog: StockItem[], stock: StockItem[]) {
  const merged = [...catalog];
  const partIds = new Set(catalog.map((item) => item.partId).filter(Boolean));
  const partCodes = new Set(catalog.map((item) => normalizedPartCode(item.partCode)).filter(Boolean));
  for (const item of stock) {
    const code = normalizedPartCode(item.partCode);
    if (partIds.has(item.partId) || (code && partCodes.has(code))) continue;
    merged.push(item);
    if (item.partId) partIds.add(item.partId);
    if (code) partCodes.add(code);
  }
  return merged;
}

export function ReplacementEditor({ stock, catalog, value, onChange }: Props) {
  const lines = value.length ? value : [newLine()];
  const removalCatalog = useMemo(() => mergeRemovalCatalog(catalog, stock), [catalog, stock]);
  useEffect(() => {
    if (!value.length) return;
    let changed = false;
    const reconciled = value.map((line) => {
      if (!line.installed || line.removed || line.removedSelectionMode === 'manual') return line;
      const catalogItem = removalCatalog.find((candidate) => candidate.partId === line.installed?.partId)
        || removalCatalog.find((candidate) => normalizedPartCode(candidate.partCode) === normalizedPartCode(line.installed?.partCode));
      if (!catalogItem) return line;
      changed = true;
      return {
        ...line,
        removed: selectedPart(removalCatalog, catalogItem.partId, line.installed.qty),
        removedSelectionMode: 'auto' as const,
      };
    });
    if (changed) onChange(reconciled);
  }, [onChange, removalCatalog, value]);
  const update = (index: number, patch: Partial<PartReplacementLine>) => {
    onChange(lines.map((line, lineIndex) => lineIndex === index ? { ...line, ...patch } : line));
  };
  return <div className="replacement-editor">
    {lines.map((line, index) => {
      const installedItem = stock.find((item) => item.partId === line.installed?.partId);
      const removedItem = removalCatalog.find((item) => item.partId === line.removed?.partId);
      const chooseInstalled = (item?: StockItem) => {
        if (!item) {
          update(index, {
            installed: undefined,
            ...(line.removedSelectionMode === 'auto' ? { removed: undefined, removedSelectionMode: undefined } : {}),
          });
          return;
        }
        const installedQty = line.installed?.partId === item.partId ? line.installed.qty : 1;
        const installed = selectedPart(stock, item.partId, installedQty);
        const mayAutoSelectRemoved = !line.removed || line.removedSelectionMode === 'auto';
        if (!mayAutoSelectRemoved) {
          update(index, { installed });
          return;
        }
        const catalogItem = removalCatalog.find((candidate) => candidate.partId === item.partId)
          || removalCatalog.find((candidate) => normalizedPartCode(candidate.partCode) === normalizedPartCode(item.partCode));
        update(index, {
          installed,
          removed: catalogItem ? selectedPart(removalCatalog, catalogItem.partId, installedQty) : undefined,
          removedSelectionMode: catalogItem ? 'auto' : undefined,
        });
      };
      return <article className="replacement-line" key={line.replacementId}>
        <div className="replacement-line__header"><strong>Înlocuire #{index + 1}</strong>{lines.length > 1 && <button type="button" onClick={() => onChange(lines.filter((_, lineIndex) => lineIndex !== index))}>Șterge</button>}</div>
        <PartCombobox label="Piesă montată din stocul meu" items={stock.filter((item) => item.qty > 0)} selectedPartId={line.installed?.partId} emptyLabel="Fără piesă montată" showQuantity onSelect={chooseInstalled} />
        {installedItem && <div className="replacement-quantity"><span>Cantitate montată</span><QuantityControl value={line.installed?.qty || 1} max={installedItem.qty} onChange={(qty) => update(index, { installed: selectedPart(stock, installedItem.partId, qty) })} /></div>}
        <PartCombobox label="Piesă defectă demontată" items={removalCatalog} selectedPartId={line.removed?.partId} emptyLabel="Fără piesă demontată" onSelect={(item) => update(index, { removed: item ? selectedPart(removalCatalog, item.partId, line.removed?.partId === item.partId ? line.removed.qty : 1) : undefined, removedSelectionMode: 'manual' })} />
        {removedItem && <div className="replacement-quantity"><span>Cantitate demontată</span><QuantityControl value={line.removed?.qty || 1} max={999} onChange={(qty) => update(index, { removed: selectedPart(removalCatalog, removedItem.partId, qty) })} /></div>}
        {line.installed && !line.removed && <div className="replacement-warning">Piesa montată nu are o piesă defectă demontată asociată. Confirmarea va fi cerută la finalizare.</div>}
        <div className="replacement-serials"><label><span>Serie piesă nouă (opțional)</span><input value={line.installedSerial || ''} onChange={(event) => update(index, { installedSerial: event.target.value })} /></label><label><span>Serie piesă defectă (opțional)</span><input value={line.removedSerial || ''} onChange={(event) => update(index, { removedSerial: event.target.value })} /></label></div>
        <label><span>Observații piesă (opțional)</span><input value={line.notes || ''} onChange={(event) => update(index, { notes: event.target.value })} /></label>
      </article>;
    })}
    <button className="btn btn-secondary block" type="button" onClick={() => onChange([...lines, newLine()])}>+ Adaugă altă înlocuire</button>
  </div>;
}
